import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { apiFetch } from '../../../../../shared/apiClient';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import {
  CommitmentScoringApi,
  type CommitmentScoringSettings,
  type CommitmentScoringOverride,
} from '../../../../../features/analytics/commitmentScoringApi';
import type { ScoringProfile } from '../../../../../domain/commitment-scoring';

interface ProductInfo {
  id: string;
  name: string;
}

const PROFILE_OPTIONS: { value: ScoringProfile; label: string; description: string }[] = [
  {
    value: 'scope_first',
    label: 'Delivery & Scope-first',
    description:
      'Commitment score uses delivery progress and scope fidelity. Schedule is informational.',
  },
  {
    value: 'date_first',
    label: 'Date-first',
    description: 'Commitment score prioritizes hitting the release window (75/25 schedule/scope).',
  },
  {
    value: 'balanced',
    label: 'Balanced (50/50)',
    description: 'Equal weight: scope and schedule contribute equally.',
  },
  {
    value: 'custom',
    label: 'Custom coefficient',
    description: 'Set your own scope/schedule weight split.',
  },
];

function weightPctForProfile(profile: ScoringProfile, custom: number): number {
  if (profile === 'scope_first') return 100;
  if (profile === 'date_first') return 25;
  if (profile === 'balanced') return 50;
  return custom;
}

export const CommitmentScoringPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [orgDefaults, setOrgDefaults] = useState<CommitmentScoringSettings>({
    profile: 'balanced',
    scopeWeight: 0.5,
    scheduleWeight: 0.5,
  });
  const [overrides, setOverrides] = useState<CommitmentScoringOverride[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [localProfile, setLocalProfile] = useState<ScoringProfile>('balanced');
  const [localCustomWeight, setLocalCustomWeight] = useState(50);

  const currentSaved = useMemo(() => {
    if (!selectedProductId) return orgDefaults;
    const o = overrides.find((ov) => ov.productId === selectedProductId);
    return o?.settings ?? orgDefaults;
  }, [selectedProductId, orgDefaults, overrides]);

  const isOverride = useMemo(
    () => !!selectedProductId && overrides.some((o) => o.productId === selectedProductId),
    [selectedProductId, overrides]
  );

  const savedCustomWeight =
    Math.round(((currentSaved.scopeWeight ?? 0.5) * 100 + Number.EPSILON) * 100) / 100;

  const hasChanges = useMemo(() => {
    if (localProfile !== (currentSaved.profile as ScoringProfile)) return true;
    if (localProfile === 'custom') {
      return Math.round(localCustomWeight) !== Math.round(savedCustomWeight);
    }
    return false;
  }, [localProfile, localCustomWeight, currentSaved, savedCustomWeight]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgRes, overridesRes, productsRes] = await Promise.all([
          CommitmentScoringApi.getOrgSettings(),
          CommitmentScoringApi.getOverrides(),
          apiFetch('/api/products'),
        ]);
        if (cancelled) return;
        setOrgDefaults(orgRes.settings);
        setOverrides(overridesRes.overrides);
        if (productsRes.ok) {
          const payload = await productsRes.json();
          setProducts(
            (payload.products ?? []).map((p: ProductInfo) => ({ id: p.id, name: p.name }))
          );
        }
        setLocalProfile((orgRes.settings.profile as ScoringProfile) ?? 'balanced');
        setLocalCustomWeight(
          Math.round(((orgRes.settings.scopeWeight ?? 0.5) * 100 + Number.EPSILON) * 100) / 100
        );
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load commitment settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const switchProduct = useCallback(
    (productId: string | null) => {
      setSelectedProductId(productId);
      setError(null);
      setSaved(false);
      const s = !productId
        ? orgDefaults
        : (overrides.find((ov) => ov.productId === productId)?.settings ?? orgDefaults);
      setLocalProfile((s.profile as ScoringProfile) ?? 'balanced');
      setLocalCustomWeight(Math.round(((s.scopeWeight ?? 0.5) * 100 + Number.EPSILON) * 100) / 100);
    },
    [orgDefaults, overrides]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const scopeWeightFrac =
        localProfile === 'custom'
          ? Math.max(0, Math.min(100, localCustomWeight)) / 100
          : weightPctForProfile(localProfile, localCustomWeight) / 100;
      const settings: CommitmentScoringSettings = {
        profile: localProfile,
        scopeWeight: scopeWeightFrac,
        scheduleWeight: 1 - scopeWeightFrac,
      };
      if (!selectedProductId) {
        const res = await CommitmentScoringApi.saveOrgSettings(settings);
        setOrgDefaults(res.settings);
      } else {
        await CommitmentScoringApi.saveOverride(selectedProductId, settings);
        const updated = await CommitmentScoringApi.getOverrides();
        setOverrides(updated.overrides);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['project-settings'] });
    } catch {
      setError('Failed to save commitment settings');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, localProfile, localCustomWeight, queryClient]);

  const handleReset = useCallback(async () => {
    if (!selectedProductId) return;
    setSaving(true);
    setError(null);
    try {
      await CommitmentScoringApi.deleteOverride(selectedProductId);
      const updated = await CommitmentScoringApi.getOverrides();
      setOverrides(updated.overrides);
      setLocalProfile((orgDefaults.profile as ScoringProfile) ?? 'balanced');
      setLocalCustomWeight(
        Math.round(((orgDefaults.scopeWeight ?? 0.5) * 100 + Number.EPSILON) * 100) / 100
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['project-settings'] });
    } catch {
      setError('Failed to reset commitment override');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, orgDefaults, queryClient]);

  if (loading) {
    return (
      <div className="settings-panel">
        <div style={{ padding: '8px 0', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
          Loading commitment settings...
        </div>
      </div>
    );
  }

  const previewScope = weightPctForProfile(localProfile, localCustomWeight);
  const previewSchedule = 100 - previewScope;

  return (
    <div className="settings-panel">
      <div className="settings-panel__header">
        <h3 className="settings-panel__title">Commitment scoring profile</h3>
        <p className="settings-panel__description">
          Choose how the commitment pillar score is computed for Delivery Review. This controls the
          balance between scope fidelity and schedule adherence.
        </p>
      </div>

      {error && (
        <div className="settings-callout settings-callout--danger" style={{ marginBottom: '16px' }}>
          <div className="settings-callout__body">{error}</div>
        </div>
      )}

      {/* Product selector */}
      <div style={{ marginTop: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
          <span style={{ fontWeight: 500 }}>Editing settings for</span>
          <CustomSelect
            value={selectedProductId ?? ''}
            options={[
              { value: '', label: 'Organization default' },
              ...products.map((p) => ({
                value: p.id,
                label: `${p.name}${overrides.some((o) => o.productId === p.id) ? ' *' : ''}`,
              })),
            ]}
            onChange={(val) => switchProduct(val || null)}
            style={{ minWidth: '180px', fontSize: '14px' }}
          />
        </label>
        {selectedProductId && !isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Using org defaults. Edit and save to create a product-specific override.
          </div>
        )}
        {isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Product-specific override active.{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>
              Org defaults: profile = {orgDefaults.profile}
              {orgDefaults.profile === 'custom'
                ? `, scope = ${Math.round((orgDefaults.scopeWeight ?? 0.5) * 100)}%`
                : ''}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
        {PROFILE_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '10px 14px',
              borderRadius: '10px',
              border: `1px solid ${localProfile === opt.value ? 'var(--color-accent)' : 'var(--glass-border)'}`,
              background: localProfile === opt.value ? 'var(--color-accent-hover)' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <input
              type="radio"
              name="commitment-profile"
              value={opt.value}
              checked={localProfile === opt.value}
              onChange={() => setLocalProfile(opt.value)}
              style={{ marginTop: '2px' }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>{opt.label}</div>
              <div
                style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' }}
              >
                {opt.description}
              </div>
            </div>
          </label>
        ))}
      </div>

      {localProfile === 'custom' && (
        <div
          style={{
            marginTop: '16px',
            padding: '14px',
            borderRadius: '10px',
            background: 'var(--glass-bg)',
          }}
        >
          <div style={{ fontSize: '13px', marginBottom: '10px', fontWeight: 600 }}>
            Custom weight: Scope {localCustomWeight}% / Schedule {100 - localCustomWeight}%
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={localCustomWeight}
            onChange={(e) => setLocalCustomWeight(Number(e.target.value))}
            style={{ width: '100%', accentColor: 'var(--color-accent)' }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: 'var(--color-text-muted)',
              marginTop: '4px',
            }}
          >
            <span>100% schedule</span>
            <span>100% scope</span>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: '16px',
          padding: '12px 14px',
          borderRadius: '8px',
          background: 'var(--color-bg-elevated)',
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
        }}
      >
        <strong>Preview:</strong> If scope = 80%, delivery = 50%, schedule = 60% → combined ={' '}
        {Math.round((previewScope / 100) * ((80 * 50) / 100) + (previewSchedule / 100) * 60)}%
      </div>

      <div
        style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        {isOverride && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={handleReset}
            title="Reset to org defaults"
          >
            <RotateCcw size={13} />
            {saving ? 'Resetting…' : 'Reset to org default'}
          </button>
        )}
        {saved && (
          <span style={{ fontSize: '12px', color: 'var(--color-success)' }}>Profile saved</span>
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </div>
    </div>
  );
};
